import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Search, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";

export default function Navbar() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => setOpen(false), [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  const links = [
    { to: "/stores", label: "Stores" },
    { to: "/categories", label: "Categories" },
    { to: "/compare", label: "Compare" },
    { to: "/blog", label: "Blog" },
    { to: "/admin", label: "Admin" },
  ];

  return (
    <header
      className={`sticky top-0 z-50 border-b transition-colors ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-slate-200" : "bg-white border-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
            <Logo size={32} />
            <span className="font-display text-xl tracking-tighter">SharePromos</span>
          </Link>

          <form onSubmit={submit} className="hidden md:flex flex-1 max-w-md mx-8" data-testid="nav-search-form">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                data-testid="nav-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search stores, brands, deals…"
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-sm text-sm focus:border-slate-950 focus:outline-none transition-colors"
              />
            </div>
          </form>

          <nav className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={`text-sm font-medium link-underline ${
                  loc.pathname.startsWith(l.to) ? "text-slate-950" : "text-slate-600 hover:text-slate-950"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <button
            className="md:hidden p-2"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {open && (
          <div className="md:hidden py-4 border-t border-slate-200 space-y-3" data-testid="mobile-menu">
            <form onSubmit={submit} className="flex mb-3">
              <input
                data-testid="mobile-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search deals…"
                className="w-full px-4 py-2 border border-slate-300 rounded-sm text-sm"
              />
            </form>
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="block py-2 text-sm font-medium"
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </header>
  );
}
